/**
 * Module-level registry of active layout-store APIs, for code that needs to
 * reach every currently-mounted layout-store without traversing React
 * context (panel titles today).
 *
 * Why a registry and not a subscription
 * =====================================
 *
 * The naive shape is a useEffect inside the workspace tree that watches
 * `useExpressionLibrary().expressions` and reconciles on change.
 * That's an anti-pattern — useEffect-to-sync-two-stores-we-own is the
 * canonical example in "You Might Not Need an Effect". The registry
 * keeps the call site imperative: we have data, we have a handle on the
 * stores that need it, we call their method. No subscription, no diff
 * inside an effect, no React lifecycle dance.
 *
 * Lifecycle
 * =========
 *
 * `LayoutStoreProvider` calls `registerLayoutStore` on mount and the
 * returned dispose function on unmount. The registry never holds dead
 * stores — when the React subtree unmounts the entry is removed.
 *
 * Test reset
 * ==========
 *
 * `__resetRegistryForTest` clears the set so a fresh `renderHook` start
 * doesn't inherit stores from a previous case.
 */

import type { StoreApi } from 'zustand'
import type { LayoutStoreState } from './store'

export type LayoutStoreApi = StoreApi<LayoutStoreState>

const stores = new Set<LayoutStoreApi>()

/**
 * Add a store to the registry. Returns the dispose function — call on
 * unmount to remove the entry. Safe to call multiple times for the same
 * store; the set semantics dedupe.
 */
export function registerLayoutStore(api: LayoutStoreApi): () => void {
  stores.add(api)
  return () => {
    stores.delete(api)
  }
}

/**
 * Snapshot of currently-active layout-stores. Returns a plain array so
 * iteration is stable even if the registry mutates mid-loop (e.g. a
 * Provider unmounts while we're walking).
 */
export function getActiveLayoutStores(): LayoutStoreApi[] {
  return Array.from(stores)
}

/** Test-only: clear the registry between cases. */
export function __resetRegistryForTest(): void {
  stores.clear()
}
